import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { CacheService } from '@uzurv/core';
import { UZURV_REST_CONFIGURATION, UzurvRestConfiguration, UzurvRestService } from '@uzurv/http';
import { Deserialize, Serialize } from 'cerialize';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RandomScreeningDriverSelection, RandomScreeningDriverSelectionInfo, RandomScreeningYearlyProgress, RuleValue } from '../../models';

@Injectable({
  providedIn: 'root'
})
export class RandomScreeningDriverSelectionService extends UzurvRestService {
  readonly resourceUrl = 'randomScreeningDriverSelection/';

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer,
    private cacheService: CacheService
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * Gets a random screening period
   * @permission screenings_manager
   */
  getRandomScreeningYearlyProgress(): Observable<RandomScreeningYearlyProgress> {
    return this.get<RandomScreeningYearlyProgress>(`${this.resourceUrl}yearProgress`).pipe(
      map(response => Deserialize(response, RandomScreeningYearlyProgress))
    );
  }

  /**
   * Lists random screening driver selections
   * @permission screenings_manager
   */

  listSelectedDrivers(): Observable<Array<RandomScreeningDriverSelectionInfo>> {
    return this.get(`${this.resourceUrl}drivers`, {
      pageNumber: 0,
      pageSize: 100
    }).pipe(map(response => Deserialize(response, RandomScreeningDriverSelectionInfo)));
  }

  /**
   * Lists random screening rule values
   * @permission screenings_manager
   */
  listConfigurationValues(): Observable<Array<RuleValue>> {
    return this.get(`${this.resourceUrl}configuration`).pipe(map(response => Deserialize(response, RuleValue)));
  }

  /**
   * Gets a randomScreeningDriverSelection by id
   *
   * @param randomScreeningDriverSelectionId
   */
  getRandomScreeningDriverSelectionById(randomScreeningDriverSelectionId: number): Observable<RandomScreeningDriverSelection> {
    return this.get(`${this.resourceUrl}${randomScreeningDriverSelectionId}`).pipe(
      map(response => Deserialize(response, RandomScreeningDriverSelection))
    );
  }

  /**
   * Update a random screening driver selection.
   *
   * @param randomScreeningDriverSelection
   */
  updateRandomScreeningDriverSelection(
    randomScreeningDriverSelection: RandomScreeningDriverSelection
  ): Observable<RandomScreeningDriverSelection> {
    return this.put<RandomScreeningDriverSelection>(
      `${this.resourceUrl}${randomScreeningDriverSelection.id}`,
      randomScreeningDriverSelection
    ).pipe(map(response => Deserialize(response, RandomScreeningDriverSelection)));
  }

  /**
   * Creates a random substance screening.
   * @param dotsDriverId
   * @param screeningPackageCode
   */
  createRandomSubstanceScreening(dotsDriverId: number, screeningPackageCode: string): Observable<RandomScreeningDriverSelection> {
    return this.post<RandomScreeningDriverSelection>(
      `${this.resourceUrl}${dotsDriverId}/createAndNotify/?screeningPackage=${screeningPackageCode}`,
      {}
    ).pipe(map(response => Deserialize(response, RandomScreeningDriverSelection)));
  }

  /**
   * Update a rule value
   *
   * @param ruleValue
   */
  updateRuleValue(ruleValue: RuleValue): Observable<RuleValue> {
    return this.put<RuleValue>(`${this.resourceUrl}ruleValue/${ruleValue.id}`, Serialize(ruleValue, RuleValue)).pipe(
      map(response => Deserialize(response, RuleValue))
    );
  }

  protected getApiUrl(): string {
    return this.uzurvRestConfiguration.dotsApiUrl;
  }
}
